import { ApplicationDataKey } from '@/constants'
import { observe } from 'jotai-effect'
import { atom, getDefaultStore } from 'jotai'
import { currentPubkeyAtom, signerAtom } from './session'
import { now } from '@/lib/utils'
import client from '@/services/client.service'
import * as kinds from '@nostr/tools/kinds'
import { atomWithStorage } from 'jotai/utils'
import { mutePubkeySetAtom, relayListAtom } from './lists'
import { useEffect, useMemo, useState } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { usePrimaryPage } from '@/PageManager'
import { hideNotificationsAtom } from '@/atoms/preferences'
import { NostrEvent } from '@nostr/tools/wasm'
import type { SubCloser } from '@nostr/tools/abstract-pool'
import { compareEvents } from '@/lib/event'
import { isMention, notificationFilter, replyKinds, reactionKinds } from '@/lib/notification'
import { pool } from '@nostr/gadgets/global'
import { loadRelayList } from '@nostr/gadgets/lists'

const store = getDefaultStore()

let lastPublishedSeenNotificationsAtEventAt = -1

export async function updateNotificationsSeenAt(skipPublish = false) {
  const signer = await store.get(signerAtom)
  if (!signer) return
  const relays = await store.get(relayListAtom)
  if (!relays) return

  const time = now()
  store.set(lastReadNotificationTimeAtom, time)

  if (
    !skipPublish &&
    (lastPublishedSeenNotificationsAtEventAt < 0 ||
      time - lastPublishedSeenNotificationsAtEventAt > 10 * 60)
  ) {
    await client.publishEvent(
      relays
        .filter((r) => r.write)
        .map((r) => r.url)
        .concat(window.hallway.universe.bigRelayUrls)
        .slice(0, 4),
      await signer.signEvent({
        kind: kinds.Application,
        content: 'Records read time to sync notification status across devices.',
        tags: [['d', ApplicationDataKey.NOTIFICATIONS_SEEN_AT]],
        created_at: now()
      })
    )
    lastPublishedSeenNotificationsAtEventAt = time
  }
}

export const lastReadNotificationTimeAtom = atomWithStorage(
  's:lastReadNotificationTime',
  0,
  undefined,
  { getOnInit: true }
)

export const readNotificationIdSetAtom = atom<Set<string>>(new Set<string>())

observe((get, set) => {
  const currentPubkey = get(currentPubkeyAtom)
  if (!currentPubkey) return
  let cancelled = false

  get(relayListAtom).then((relays) => {
    if (cancelled) return
    if (!relays) return
    ;(async () => {
      const events = await client.fetchEvents(
        relays
          .filter((r) => r.write)
          .map((r) => r.url)
          .concat(window.hallway.universe.bigRelayUrls)
          .slice(0, 3),
        {
          kinds: [kinds.Application],
          authors: [currentPubkey],
          '#d': [ApplicationDataKey.NOTIFICATIONS_SEEN_AT],
          limit: 1
        }
      )
      if (cancelled) return

      const sortedEvents = events.sort((a, b) => b.created_at - a.created_at)
      const lastRead = sortedEvents.length ? sortedEvents[0].created_at : now()

      set(lastReadNotificationTimeAtom, lastRead)
    })()
  })

  return () => {
    cancelled = true
  }
})

export const filteredNewNotificationsAtom = atom(0)
export const filteredNewConversationsAtom = atom(0)

export function useNotificationProcessor() {
  const { current } = usePrimaryPage()
  const active = useMemo(
    () => current === 'notifications' || current === 'conversations',
    [current]
  )
  const pubkey = useAtomValue(currentPubkeyAtom)
  const lastReadNotificationTime = useAtomValue(lastReadNotificationTimeAtom)
  const mutePubkeySet = useAtomValue(mutePubkeySetAtom)
  const [newNotifications, setNewNotifications] = useState<NostrEvent[]>([])
  const setFilteredNewNotifications = useSetAtom(filteredNewNotificationsAtom)
  const setFilteredNewConversations = useSetAtom(filteredNewConversationsAtom)
  const filteredNewNotifications = useAtomValue(filteredNewNotificationsAtom)
  const hideNotifications = useAtomValue(hideNotificationsAtom)

  useEffect(() => {
    if (active || lastReadNotificationTime <= 0 || !pubkey) {
      setFilteredNewNotifications(0)
      setFilteredNewConversations(0)
      return
    }

    ;(async () => {
      let fn = 0
      let fc = 0

      for (const notification of newNotifications) {
        if (notification.created_at <= lastReadNotificationTime) {
          break
        }
        if (notification.pubkey === pubkey) {
          continue
        }
        if (!notificationFilter(notification, { pubkey, mutePubkeySet })) {
          continue
        }

        if ((replyKinds as number[]).includes(notification.kind)) {
          fc++
          if (await isMention(notification, pubkey)) fn++
        } else {
          fn++
        }
      }

      setFilteredNewNotifications(fn)
      setFilteredNewConversations(fc)
    })()
  }, [newNotifications, lastReadNotificationTime, mutePubkeySet, active, pubkey])

  useEffect(() => {
    setNewNotifications([])
    updateNotificationsSeenAt(!active)
  }, [active])

  useEffect(() => {
    if (!pubkey || hideNotifications) return

    store.set(readNotificationIdSetAtom, new Set<string>())

    const isMountedRef = { current: true }
    const subCloserRef: { current: SubCloser | null } = { current: null }

    const subscribe = async () => {
      if (subCloserRef.current) {
        subCloserRef.current.close()
        subCloserRef.current = null
      }
      if (!isMountedRef.current) return null
      setNewNotifications([])

      const { items } = await loadRelayList(pubkey)
      const relays = items
        .filter((r) => r.read)
        .map((r) => r.url)
        .concat(window.hallway.universe.readRepliesFrom || window.hallway.universe.bigRelayUrls)

      try {
        let eosed = false
        const subCloser = pool.subscribe(
          relays,
          {
            kinds: [...replyKinds, ...reactionKinds],
            '#p': [pubkey],
            limit: 20
          },
          {
            label: 'f-notifications',
            oneose: () => {
              eosed = true
              setNewNotifications((prev) => [...prev.sort((a, b) => compareEvents(b, a))])
            },
            onevent: (evt) => {
              if (evt.pubkey !== pubkey) {
                setNewNotifications((prev) => {
                  if (!eosed) return [evt, ...prev]
                  if (prev.length && compareEvents(prev[0], evt) >= 0) return prev
                  return [evt, ...prev]
                })
                client.emitNewEvent(evt)
              }
            },
            onclose: (reasons) => {
              if (reasons.every((reason) => reason.reason === 'closed by caller')) return
              if (isMountedRef.current) {
                setTimeout(() => {
                  if (isMountedRef.current) subscribe()
                }, 5_000)
              }
            }
          }
        )

        subCloserRef.current = subCloser
        return subCloser
      } catch (error) {
        console.error('Subscription error:', error)
        if (isMountedRef.current) {
          setTimeout(() => {
            if (isMountedRef.current) subscribe()
          }, 5_000)
        }
        return null
      }
    }

    subscribe()

    return () => {
      isMountedRef.current = false
      if (subCloserRef.current) {
        subCloserRef.current.close()
        subCloserRef.current = null
      }
    }
  }, [pubkey, hideNotifications])

  useEffect(() => {
    if (filteredNewNotifications > 0) {
      document.title = `(${filteredNewNotifications > 99 ? '99+' : filteredNewNotifications}) ${window.hallway.universe.appName}`
    } else {
      document.title = window.hallway.universe.appName
    }

    const favicons = document.querySelectorAll<HTMLLinkElement>("link[rel*='icon']")
    if (!favicons.length) return

    if (filteredNewNotifications === 0) {
      for (const favicon of favicons) {
        favicon.href = '/favicon.ico'
      }
    } else {
      let cancelled = false
      const img = document.createElement('img')
      img.src = '/favicon.ico'
      img.onload = () => {
        if (cancelled) return
        const size = Math.max(img.width, img.height, 32)
        const canvas = document.createElement('canvas')
        canvas.width = size
        canvas.height = size
        const ctx = canvas.getContext('2d')
        if (!ctx) return
        ctx.drawImage(img, 0, 0, size, size)
        const r = size * 0.16
        ctx.beginPath()
        ctx.arc(size - r - 3, r + 25, r, 0, 2 * Math.PI)
        ctx.fillStyle = '#FF0000'
        ctx.fill()
        for (const favicon of favicons) {
          favicon.href = canvas.toDataURL('image/png')
        }
      }
      return () => {
        cancelled = true
      }
    }
  }, [filteredNewNotifications])
}
